import { supabase } from './supabase';
import { BowelMovement } from '../types';
import { DaySummary } from './diseaseStateReportService';
import { dailyTrackingService } from './dailyTrackingService';
import { myMedicationService } from './myMedicationService';
import { toDateOnlyLocal, isMedicationActive } from '../utils/dateHelpers';

export type MedicationHistoryConclusion = 
  | 'improvement' 
  | 'no_change' 
  | 'worsening' 
  | 'insufficient_data';

export interface MedicationHistoryImpact {
  medicationId: string;
  medicationName: string;
  startDate: string;
  endDate?: string;
  status: 'active' | 'completed';
  
  // Okres przed rozpoczęciem leku (7-14 dni przed startDate)
  beforePeriod: {
    daysWithFlare: number;
    avgBmPerDay: number;
    bloodDaysCount: number;
    avgPain: number;
    avgUrgency: number;
    avgEnergy: number;
    periodStart: string;
    periodEnd: string;
  };
  
  // Okres po rozpoczęciu leku (14-30 dni po startDate)
  afterPeriod: {
    daysWithFlare: number;
    avgBmPerDay: number;
    bloodDaysCount: number;
    avgPain: number;
    avgUrgency: number;
    avgEnergy: number;
    periodStart: string;
    periodEnd: string;
  };
  
  // Obserwowana zmiana
  change: {
    flareDaysChange: number;
    avgBmChange: number;
    bloodDaysChange: number;
    avgPainChange: number;
    avgUrgencyChange: number;
    avgEnergyChange: number;
  };
  
  conclusion: MedicationHistoryConclusion;
  summary: string; // Opisowa analiza obserwacji
  hasEnoughData: boolean;
  
  // Analiza kombinacji leków
  combinationAnalysis?: MedicationCombinationAnalysis;
}

export interface MedicationCombinationAnalysis {
  hasCombinations: boolean; // Czy lek był kiedykolwiek stosowany w połączeniu
  combinations: MedicationCombination[];
  summary: string; // Ogólne podsumowanie
}

export interface MedicationCombination {
  combinationName: string; // np. "Lek A + Lek B"
  otherMedications: string[]; // Lista nazw innych leków w kombinacji
  
  // Okres samodzielny
  soloPeriod: {
    daysWithFlare: number;
    avgBmPerDay: number;
    bloodDaysCount: number;
    avgPain: number;
    avgUrgency: number;
    avgEnergy: number;
    periodStart: string;
    periodEnd: string;
    daysCount: number;
  };
  
  // Okres w połączeniu
  combinationPeriod: {
    daysWithFlare: number;
    avgBmPerDay: number;
    bloodDaysCount: number;
    avgPain: number;
    avgUrgency: number;
    avgEnergy: number;
    periodStart: string;
    periodEnd: string;
    daysCount: number;
  };
  
  // Różnica (combination - solo)
  difference: {
    flareDaysChange: number;
    avgBmChange: number;
    bloodDaysChange: number;
    avgPainChange: number;
    avgUrgencyChange: number;
    avgEnergyChange: number;
  };
  
  conclusion: 'improvement' | 'no_change' | 'worsening' | 'insufficient_data';
  summary: string; // Opisowy wniosek
}

// Funkcja sprawdzająca, czy dzień spełnia kryteria zaostrzenia
function isFlareDay(day: DaySummary): boolean {
  if (day.bloodPresentDay) return true;
  if (day.bmCount >= 6) return true;
  if (day.painAvgDay >= 3) return true;
  if (day.urgencyAvgDay >= 4) return true;
  return false;
}

// Funkcja generująca DaySummary dla zakresu dat
async function getDaySummaries(userId: string, dateFrom: string, dateTo: string): Promise<DaySummary[]> {
  const { data: movements, error } = await supabase
    .from('bowel_movements')
    .select('*')
    .eq('user_id', userId)
    .gte('date', dateFrom)
    .lte('date', dateTo)
    .order('date', { ascending: true })
    .order('time', { ascending: true });
  
  if (error) {
    throw error;
  }
  
  const bowelMovements: BowelMovement[] = (movements || []).map((m: any) => ({
    id: m.id,
    date: m.date,
    time: m.time,
    consistency: m.consistency,
    hasBlood: m.has_blood,
    bloodAmount: m.blood_amount,
    bloodType: m.blood_type,
    hasMucus: m.has_mucus,
    mucusAmount: m.mucus_amount,
    pain: m.pain || 0,
    amount: m.amount,
    hasGas: m.has_gas,
    gasIntensity: m.gas_intensity,
    urgency: m.urgency || 1,
    otherSymptoms: m.other_symptoms || ''
  }));
  
  // Generuj wszystkie dni w okresie
  const startDate = new Date(dateFrom + 'T12:00:00');
  const endDate = new Date(dateTo + 'T12:00:00');
  const days: string[] = [];
  const currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    days.push(toDateOnlyLocal(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // Agregacja dzienna
  return days.map(date => {
    const dayMovements = bowelMovements.filter(bm => bm.date === date);
    
    const bmCount = dayMovements.length;
    const bloodPresentDay = dayMovements.some(bm => bm.hasBlood);
    const painAvgDay = dayMovements.length > 0
      ? dayMovements.reduce((sum, bm) => sum + (bm.pain || 0), 0) / dayMovements.length
      : 0;
    const urgencyAvgDay = dayMovements.length > 0
      ? dayMovements.reduce((sum, bm) => sum + (bm.urgency || 1), 0) / dayMovements.length
      : 0;
    
    return {
      date,
      bmCount,
      bloodPresentDay,
      painAvgDay,
      urgencyAvgDay,
      mucusPresentDay: false,
      gasPresentDay: false,
      severityScore: 0
    };
  });
}

// Funkcja określająca wniosek historyczny na podstawie zmian
function determineConclusion(
  change: MedicationHistoryImpact['change'],
  hasEnoughData: boolean
): MedicationHistoryConclusion {
  if (!hasEnoughData) {
    return 'insufficient_data';
  }
  
  // Liczba pozytywnych zmian (poprawa)
  let positiveChanges = 0;
  let negativeChanges = 0;
  
  // Dni zaostrzeń - mniej = lepiej
  if (change.flareDaysChange < -1) positiveChanges++;
  else if (change.flareDaysChange > 1) negativeChanges++;
  
  // Średnia liczba wypróżnień - mniej = lepiej (jeśli zmiana znacząca)
  if (change.avgBmChange < -0.5) positiveChanges++;
  else if (change.avgBmChange > 0.5) negativeChanges++;
  
  // Dni z krwią - mniej = lepiej
  if (change.bloodDaysChange < 0) positiveChanges++;
  else if (change.bloodDaysChange > 0) negativeChanges++;
  
  // Średni ból - mniej = lepiej (jeśli zmiana znacząca)
  if (change.avgPainChange < -0.5) positiveChanges++;
  else if (change.avgPainChange > 0.5) negativeChanges++;
  
  // Średnia pilność - mniej = lepiej (jeśli zmiana znacząca)
  if (change.avgUrgencyChange < -0.5) positiveChanges++;
  else if (change.avgUrgencyChange > 0.5) negativeChanges++;
  
  // Średni poziom energii - więcej = lepiej (jeśli zmiana znacząca)
  if (change.avgEnergyChange > 0.5) positiveChanges++;
  else if (change.avgEnergyChange < -0.5) negativeChanges++;
  
  // Określ wniosek
  if (positiveChanges >= 3 && negativeChanges <= 1) {
    return 'improvement';
  } else if (negativeChanges >= 3 && positiveChanges <= 1) {
    return 'worsening';
  } else {
    return 'no_change';
  }
}

// Funkcja generująca opisową analizę obserwacji
function generateSummary(
  medicationName: string,
  change: MedicationHistoryImpact['change'],
  conclusion: MedicationHistoryConclusion
): string {
  if (conclusion === 'insufficient_data') {
    return 'Za mało danych, aby ocenić wpływ leczenia.';
  }
  
  const changes: string[] = [];
  
  if (Math.abs(change.flareDaysChange) > 0) {
    if (change.flareDaysChange < 0) {
      changes.push(`liczba dni zaostrzeń zmniejszyła się o ${Math.abs(change.flareDaysChange)}`);
    } else {
      changes.push(`liczba dni zaostrzeń zwiększyła się o ${change.flareDaysChange}`);
    }
  }
  
  if (Math.abs(change.avgBmChange) > 0.5) {
    if (change.avgBmChange < 0) {
      changes.push(`średnia liczba wypróżnień zmniejszyła się o ${Math.abs(change.avgBmChange).toFixed(1)}`);
    } else {
      changes.push(`średnia liczba wypróżnień zwiększyła się o ${change.avgBmChange.toFixed(1)}`);
    }
  }
  
  if (Math.abs(change.bloodDaysChange) > 0) {
    if (change.bloodDaysChange < 0) {
      changes.push(`liczba dni z krwią zmniejszyła się o ${Math.abs(change.bloodDaysChange)}`);
    } else {
      changes.push(`liczba dni z krwią zwiększyła się o ${change.bloodDaysChange}`);
    }
  }
  
  if (Math.abs(change.avgPainChange) > 0.5) {
    if (change.avgPainChange < 0) {
      changes.push(`średni ból zmniejszył się o ${Math.abs(change.avgPainChange).toFixed(1)}`);
    } else {
      changes.push(`średni ból zwiększył się o ${change.avgPainChange.toFixed(1)}`);
    }
  }
  
  if (Math.abs(change.avgUrgencyChange) > 0.5) {
    if (change.avgUrgencyChange < 0) {
      changes.push(`średnia pilność zmniejszyła się o ${Math.abs(change.avgUrgencyChange).toFixed(1)}`);
    } else {
      changes.push(`średnia pilność zwiększyła się o ${change.avgUrgencyChange.toFixed(1)}`);
    }
  }
  
  if (Math.abs(change.avgEnergyChange) > 0.5) {
    if (change.avgEnergyChange > 0) {
      changes.push(`średni poziom energii zwiększył się o ${change.avgEnergyChange.toFixed(1)}`);
    } else {
      changes.push(`średni poziom energii zmniejszył się o ${Math.abs(change.avgEnergyChange).toFixed(1)}`);
    }
  }
  
  if (changes.length > 0) {
    return `Po włączeniu leku ${medicationName} obserwowano następujące zmiany: ${changes.slice(0, 3).join(', ')}.`;
  } else {
    return `Po włączeniu leku ${medicationName} nie zaobserwowano wyraźnych zmian w obserwowanych parametrach.`;
  }
}

// Funkcja analizująca historię wpływu wszystkich leków
async function analyzeMedicationHistory(userId: string): Promise<MedicationHistoryImpact[]> {
  try {
    // Pobierz wszystkie leki użytkownika (tylko LEK, nie SUPLEMENT)
    const medications = await myMedicationService.getAll();
    const medicationsOnly = medications.filter(med => med.category === 'LEK');
    
    const impacts: MedicationHistoryImpact[] = [];
    
    for (const medication of medicationsOnly) {
      const startDate = new Date(medication.startDate + 'T12:00:00');
      
      // Okres przed rozpoczęciem leku (14 dni przed startDate)
      const beforeStart = new Date(startDate);
      beforeStart.setDate(beforeStart.getDate() - 14);
      const beforeEnd = new Date(startDate);
      beforeEnd.setDate(beforeEnd.getDate() - 1);
      
      // Okres po rozpoczęciu leku (30 dni po startDate, lub do endDate jeśli wcześniej)
      const afterStart = new Date(startDate);
      afterStart.setDate(afterStart.getDate() + 1);
      const afterEnd = medication.endDate 
        ? new Date(Math.min(new Date(medication.endDate + 'T12:00:00').getTime(), new Date(startDate).getTime() + 30 * 24 * 60 * 60 * 1000))
        : new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const beforeStartStr = toDateOnlyLocal(beforeStart);
      const beforeEndStr = toDateOnlyLocal(beforeEnd);
      const afterStartStr = toDateOnlyLocal(afterStart);
      const afterEndStr = toDateOnlyLocal(afterEnd);
      
      // Pobierz dane dla obu okresów
      const beforeDays = await getDaySummaries(userId, beforeStartStr, beforeEndStr);
      const afterDays = await getDaySummaries(userId, afterStartStr, afterEndStr);
      
      // Sprawdź czy mamy wystarczająco danych (minimum 7 dni w każdym okresie)
      if (beforeDays.length < 7 || afterDays.length < 7) {
        impacts.push({
          medicationId: medication.id,
          medicationName: medication.name,
          startDate: medication.startDate,
          endDate: medication.endDate,
          status: isMedicationActive(medication) ? 'active' : 'completed',
          beforePeriod: {
            daysWithFlare: 0,
            avgBmPerDay: 0,
            bloodDaysCount: 0,
            avgPain: 0,
            avgUrgency: 0,
            avgEnergy: 0,
            periodStart: beforeStartStr,
            periodEnd: beforeEndStr
          },
          afterPeriod: {
            daysWithFlare: 0,
            avgBmPerDay: 0,
            bloodDaysCount: 0,
            avgPain: 0,
            avgUrgency: 0,
            avgEnergy: 0,
            periodStart: afterStartStr,
            periodEnd: afterEndStr
          },
          change: {
            flareDaysChange: 0,
            avgBmChange: 0,
            bloodDaysChange: 0,
            avgPainChange: 0,
            avgUrgencyChange: 0,
            avgEnergyChange: 0
          },
          conclusion: 'insufficient_data',
          summary: 'Za mało danych, aby ocenić wpływ leczenia.',
          hasEnoughData: false
        });
        continue;
      }
      
      // Oblicz metryki dla okresu przed
      const beforeFlareDays = beforeDays.filter(d => isFlareDay(d)).length;
      const beforeAvgBm = beforeDays.length > 0
        ? beforeDays.reduce((sum, d) => sum + d.bmCount, 0) / beforeDays.length
        : 0;
      const beforeBloodDays = beforeDays.filter(d => d.bloodPresentDay).length;
      const beforeAvgPain = beforeDays.length > 0
        ? beforeDays.reduce((sum, d) => sum + d.painAvgDay, 0) / beforeDays.length
        : 0;
      const beforeAvgUrgency = beforeDays.length > 0
        ? beforeDays.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / beforeDays.length
        : 0;
      
      // Pobierz dane samopoczucia dla okresu przed
      const beforeWellbeing = await dailyTrackingService.getRange(userId, beforeStartStr, beforeEndStr);
      const beforeEnergyLevels = beforeWellbeing
        .filter(w => w.energyLevel !== undefined)
        .map(w => w.energyLevel || 0);
      const beforeAvgEnergy = beforeEnergyLevels.length > 0
        ? beforeEnergyLevels.reduce((sum, e) => sum + e, 0) / beforeEnergyLevels.length
        : 0;
      
      // Oblicz metryki dla okresu po
      const afterFlareDays = afterDays.filter(d => isFlareDay(d)).length;
      const afterAvgBm = afterDays.length > 0
        ? afterDays.reduce((sum, d) => sum + d.bmCount, 0) / afterDays.length
        : 0;
      const afterBloodDays = afterDays.filter(d => d.bloodPresentDay).length;
      const afterAvgPain = afterDays.length > 0
        ? afterDays.reduce((sum, d) => sum + d.painAvgDay, 0) / afterDays.length
        : 0;
      const afterAvgUrgency = afterDays.length > 0
        ? afterDays.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / afterDays.length
        : 0;
      
      // Pobierz dane samopoczucia dla okresu po
      const afterWellbeing = await dailyTrackingService.getRange(userId, afterStartStr, afterEndStr);
      const afterEnergyLevels = afterWellbeing
        .filter(w => w.energyLevel !== undefined)
        .map(w => w.energyLevel || 0);
      const afterAvgEnergy = afterEnergyLevels.length > 0
        ? afterEnergyLevels.reduce((sum, e) => sum + e, 0) / afterEnergyLevels.length
        : 0;
      
      // Oblicz zmiany
      const flareDaysChange = afterFlareDays - beforeFlareDays;
      const avgBmChange = afterAvgBm - beforeAvgBm;
      const bloodDaysChange = afterBloodDays - beforeBloodDays;
      const avgPainChange = afterAvgPain - beforeAvgPain;
      const avgUrgencyChange = afterAvgUrgency - beforeAvgUrgency;
      const avgEnergyChange = afterAvgEnergy - beforeAvgEnergy;
      
      const change = {
        flareDaysChange,
        avgBmChange,
        bloodDaysChange,
        avgPainChange,
        avgUrgencyChange,
        avgEnergyChange
      };
      
      const conclusion = determineConclusion(change, true);
      const summary = generateSummary(medication.name, change, conclusion);
      
      // Analiza kombinacji leków
      const combinationAnalysis = await analyzeMedicationCombinations(
        userId,
        medication,
        medicationsOnly
      );
      
      impacts.push({
        medicationId: medication.id,
        medicationName: medication.name,
        startDate: medication.startDate,
        endDate: medication.endDate,
        status: isMedicationActive(medication) ? 'active' : 'completed',
        beforePeriod: {
          daysWithFlare: beforeFlareDays,
          avgBmPerDay: beforeAvgBm,
          bloodDaysCount: beforeBloodDays,
          avgPain: beforeAvgPain,
          avgUrgency: beforeAvgUrgency,
          avgEnergy: beforeAvgEnergy,
          periodStart: beforeStartStr,
          periodEnd: beforeEndStr
        },
        afterPeriod: {
          daysWithFlare: afterFlareDays,
          avgBmPerDay: afterAvgBm,
          bloodDaysCount: afterBloodDays,
          avgPain: afterAvgPain,
          avgUrgency: afterAvgUrgency,
          avgEnergy: afterAvgEnergy,
          periodStart: afterStartStr,
          periodEnd: afterEndStr
        },
        change,
        conclusion,
        summary,
        hasEnoughData: true,
        combinationAnalysis
      });
    }
    
    return impacts;
  } catch (error) {
    console.error('Błąd analizy historii leków:', error);
    return [];
  }
}

// Funkcja analizująca historię wpływu suplementów (category = SUPLEMENT)
async function analyzeSupplementHistory(userId: string): Promise<MedicationHistoryImpact[]> {
  try {
    // Pobierz wszystkie suplementy użytkownika (tylko SUPLEMENT, nie LEK)
    const medications = await myMedicationService.getAll();
    const supplementsOnly = medications.filter(med => med.category === 'SUPLEMENT');
    
    const impacts: MedicationHistoryImpact[] = [];
    
    for (const supplement of supplementsOnly) {
      const startDate = new Date(supplement.startDate + 'T12:00:00');
      
      // Okres przed rozpoczęciem suplementu (14 dni przed startDate)
      const beforeStart = new Date(startDate);
      beforeStart.setDate(beforeStart.getDate() - 14);
      const beforeEnd = new Date(startDate);
      beforeEnd.setDate(beforeEnd.getDate() - 1);
      
      // Okres po rozpoczęciu suplementu (30 dni po startDate, lub do endDate jeśli wcześniej)
      const afterStart = new Date(startDate);
      afterStart.setDate(afterStart.getDate() + 1);
      const afterEnd = supplement.endDate 
        ? new Date(Math.min(new Date(supplement.endDate + 'T12:00:00').getTime(), new Date(startDate).getTime() + 30 * 24 * 60 * 60 * 1000))
        : new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const beforeStartStr = beforeStart.toISOString().split('T')[0];
      const beforeEndStr = beforeEnd.toISOString().split('T')[0];
      const afterStartStr = afterStart.toISOString().split('T')[0];
      const afterEndStr = afterEnd.toISOString().split('T')[0];
      
      // Pobierz dane dla obu okresów
      const beforeDays = await getDaySummaries(userId, beforeStartStr, beforeEndStr);
      const afterDays = await getDaySummaries(userId, afterStartStr, afterEndStr);
      
      // Sprawdź czy mamy wystarczająco danych (minimum 7 dni w każdym okresie)
      if (beforeDays.length < 7 || afterDays.length < 7) {
        impacts.push({
          medicationId: supplement.id,
          medicationName: supplement.name,
          startDate: supplement.startDate,
          endDate: supplement.endDate,
          status: isMedicationActive(supplement) ? 'active' : 'completed',
          beforePeriod: {
            daysWithFlare: 0,
            avgBmPerDay: 0,
            bloodDaysCount: 0,
            avgPain: 0,
            avgUrgency: 0,
            avgEnergy: 0,
            periodStart: beforeStartStr,
            periodEnd: beforeEndStr
          },
          afterPeriod: {
            daysWithFlare: 0,
            avgBmPerDay: 0,
            bloodDaysCount: 0,
            avgPain: 0,
            avgUrgency: 0,
            avgEnergy: 0,
            periodStart: afterStartStr,
            periodEnd: afterEndStr
          },
          change: {
            flareDaysChange: 0,
            avgBmChange: 0,
            bloodDaysChange: 0,
            avgPainChange: 0,
            avgUrgencyChange: 0,
            avgEnergyChange: 0
          },
          conclusion: 'insufficient_data',
          summary: 'Za mało danych, aby ocenić obserwacje dla tego suplementu.',
          hasEnoughData: false
        });
        continue;
      }
      
      // Oblicz metryki dla okresu przed
      const beforeFlareDays = beforeDays.filter(d => isFlareDay(d)).length;
      const beforeAvgBm = beforeDays.length > 0
        ? beforeDays.reduce((sum, d) => sum + d.bmCount, 0) / beforeDays.length
        : 0;
      const beforeBloodDays = beforeDays.filter(d => d.bloodPresentDay).length;
      const beforeAvgPain = beforeDays.length > 0
        ? beforeDays.reduce((sum, d) => sum + d.painAvgDay, 0) / beforeDays.length
        : 0;
      const beforeAvgUrgency = beforeDays.length > 0
        ? beforeDays.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / beforeDays.length
        : 0;
      
      // Pobierz dane samopoczucia dla okresu przed
      const beforeWellbeing = await dailyTrackingService.getRange(userId, beforeStartStr, beforeEndStr);
      const beforeEnergyLevels = beforeWellbeing
        .filter(w => w.energyLevel !== undefined)
        .map(w => w.energyLevel || 0);
      const beforeAvgEnergy = beforeEnergyLevels.length > 0
        ? beforeEnergyLevels.reduce((sum, e) => sum + e, 0) / beforeEnergyLevels.length
        : 0;
      
      // Oblicz metryki dla okresu po
      const afterFlareDays = afterDays.filter(d => isFlareDay(d)).length;
      const afterAvgBm = afterDays.length > 0
        ? afterDays.reduce((sum, d) => sum + d.bmCount, 0) / afterDays.length
        : 0;
      const afterBloodDays = afterDays.filter(d => d.bloodPresentDay).length;
      const afterAvgPain = afterDays.length > 0
        ? afterDays.reduce((sum, d) => sum + d.painAvgDay, 0) / afterDays.length
        : 0;
      const afterAvgUrgency = afterDays.length > 0
        ? afterDays.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / afterDays.length
        : 0;
      
      // Pobierz dane samopoczucia dla okresu po
      const afterWellbeing = await dailyTrackingService.getRange(userId, afterStartStr, afterEndStr);
      const afterEnergyLevels = afterWellbeing
        .filter(w => w.energyLevel !== undefined)
        .map(w => w.energyLevel || 0);
      const afterAvgEnergy = afterEnergyLevels.length > 0
        ? afterEnergyLevels.reduce((sum, e) => sum + e, 0) / afterEnergyLevels.length
        : 0;
      
      // Oblicz zmiany
      const flareDaysChange = afterFlareDays - beforeFlareDays;
      const avgBmChange = afterAvgBm - beforeAvgBm;
      const bloodDaysChange = afterBloodDays - beforeBloodDays;
      const avgPainChange = afterAvgPain - beforeAvgPain;
      const avgUrgencyChange = afterAvgUrgency - beforeAvgUrgency;
      const avgEnergyChange = afterAvgEnergy - beforeAvgEnergy;
      
      const change = {
        flareDaysChange,
        avgBmChange,
        bloodDaysChange,
        avgPainChange,
        avgUrgencyChange,
        avgEnergyChange
      };
      
      const conclusion = determineConclusion(change, true);
      
      // Generuj podsumowanie obserwacyjne dla suplementów (inny język)
      const changes: string[] = [];
      if (Math.abs(flareDaysChange) > 0) {
        if (flareDaysChange < 0) {
          changes.push(`częściej występowało mniej dni zaostrzeń (różnica: ${Math.abs(flareDaysChange)})`);
        } else {
          changes.push(`częściej występowało więcej dni zaostrzeń (różnica: ${flareDaysChange})`);
        }
      }
      if (Math.abs(avgBmChange) > 0.5) {
        if (avgBmChange < 0) {
          changes.push(`częściej występowała mniejsza liczba wypróżnień (różnica: ${Math.abs(avgBmChange).toFixed(1)})`);
        } else {
          changes.push(`częściej występowała większa liczba wypróżnień (różnica: ${avgBmChange.toFixed(1)})`);
        }
      }
      if (Math.abs(bloodDaysChange) > 0) {
        if (bloodDaysChange < 0) {
          changes.push(`częściej występowało mniej dni z krwią (różnica: ${Math.abs(bloodDaysChange)})`);
        } else {
          changes.push(`częściej występowało więcej dni z krwią (różnica: ${bloodDaysChange})`);
        }
      }
      
      let summary = '';
      if (changes.length > 0) {
        summary = `Po rozpoczęciu suplementu ${supplement.name} obserwowano następujące zmiany: ${changes.slice(0, 2).join(', ')}. To możliwe powiązanie, nie przyczyna.`;
      } else {
        summary = `Po rozpoczęciu suplementu ${supplement.name} nie zaobserwowano wyraźnych zmian w obserwowanych parametrach.`;
      }
      
      impacts.push({
        medicationId: supplement.id,
        medicationName: supplement.name,
        startDate: supplement.startDate,
        endDate: supplement.endDate,
        status: isMedicationActive(supplement) ? 'active' : 'completed',
        beforePeriod: {
          daysWithFlare: beforeFlareDays,
          avgBmPerDay: beforeAvgBm,
          bloodDaysCount: beforeBloodDays,
          avgPain: beforeAvgPain,
          avgUrgency: beforeAvgUrgency,
          avgEnergy: beforeAvgEnergy,
          periodStart: beforeStartStr,
          periodEnd: beforeEndStr
        },
        afterPeriod: {
          daysWithFlare: afterFlareDays,
          avgBmPerDay: afterAvgBm,
          bloodDaysCount: afterBloodDays,
          avgPain: afterAvgPain,
          avgUrgency: afterAvgUrgency,
          avgEnergy: afterAvgEnergy,
          periodStart: afterStartStr,
          periodEnd: afterEndStr
        },
        change,
        conclusion,
        summary,
        hasEnoughData: true
      });
    }
    
    return impacts;
  } catch (error) {
    console.error('Błąd analizy historii suplementów:', error);
    return [];
  }
}

// Interfejs dla zakresu aktywności leku
interface ActiveRange {
  activeFrom: Date;
  activeTo: Date;
  isValid: boolean;
}

// Funkcja pomocnicza do wyznaczania zakresu aktywności leku
function getActiveRange(medication: any): ActiveRange {
  // Jeśli brak startDate, zakres jest nieprawidłowy
  if (!medication.startDate) {
    return {
      activeFrom: new Date(),
      activeTo: new Date(),
      isValid: false
    };
  }
  
  const startDate = new Date(medication.startDate + 'T12:00:00');
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Koniec dnia dzisiejszego
  
  switch (medication.type) {
    case 'permanent':
      // Na stałe: activeFrom = start_date, activeTo = today
      return {
        activeFrom: startDate,
        activeTo: today,
        isValid: true
      };
      
    case 'temporary':
      // Czasowe: activeFrom = start_date, activeTo = end_date (włącznie)
      if (!medication.endDate) {
        // Jeśli brak endDate dla temporary, użyj today
        return {
          activeFrom: startDate,
          activeTo: today,
          isValid: true
        };
      }
      const endDate = new Date(medication.endDate + 'T12:00:00'); // Koniec dnia - T12 unika UTC, zakres inclusive
      return {
        activeFrom: startDate,
        activeTo: endDate,
        isValid: true
      };
      
    case 'tapering':
      // Tapering: activeFrom = min(schedule.startDate), activeTo = max(schedule.endDate || today)
      if (!medication.taperingSchedules || medication.taperingSchedules.length === 0) {
        return {
          activeFrom: startDate,
          activeTo: today,
          isValid: true
        };
      }
      
      const schedules = medication.taperingSchedules;
      const startDates = schedules.map((s: any) => new Date(s.startDate + 'T12:00:00'));
      const endDates = schedules.map((s: any) => 
        s.endDate ? new Date(s.endDate + 'T23:59:59') : today
      );
      
      return {
        activeFrom: new Date(Math.min(...startDates.map((d: Date) => d.getTime()))),
        activeTo: new Date(Math.max(...endDates.map((d: Date) => d.getTime()))),
        isValid: true
      };
      
    case 'one-time':
      // Jednorazowe: activeFrom = oneTimeDate, activeTo = oneTimeDate (ten sam dzień)
      if (!medication.oneTimeDate) {
        return {
          activeFrom: startDate,
          activeTo: startDate,
          isValid: true
        };
      }
      const oneTimeDate = new Date(medication.oneTimeDate + 'T23:59:59');
      return {
        activeFrom: oneTimeDate,
        activeTo: oneTimeDate,
        isValid: true
      };
      
    default:
      return {
        activeFrom: startDate,
        activeTo: today,
        isValid: true
      };
  }
}

// Funkcja obliczająca liczbę dni między dwiema datami (inclusive)
function daysBetween(startDate: Date, endDate: Date): number {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // +1 bo inclusive
}

// Funkcja obliczająca przecięcie dwóch zakresów dat
function getRangeIntersection(rangeA: ActiveRange, rangeB: ActiveRange): { start: Date; end: Date; days: number } | null {
  if (!rangeA.isValid || !rangeB.isValid) {
    return null;
  }
  
  // Sprawdź czy zakresy się nakładają
  if (!rangesOverlap(rangeA, rangeB)) {
    return null;
  }
  
  // Przecięcie: max(Afrom, Bfrom) do min(Ato, Bto)
  const intersectionStart = rangeA.activeFrom > rangeB.activeFrom ? rangeA.activeFrom : rangeB.activeFrom;
  const intersectionEnd = rangeA.activeTo < rangeB.activeTo ? rangeA.activeTo : rangeB.activeTo;
  
  const days = daysBetween(intersectionStart, intersectionEnd);
  
  return {
    start: intersectionStart,
    end: intersectionEnd,
    days
  };
}

// Funkcja sprawdzająca czy dwa zakresy się nakładają (inclusive)
function rangesOverlap(rangeA: ActiveRange, rangeB: ActiveRange): boolean {
  if (!rangeA.isValid || !rangeB.isValid) {
    return false;
  }
  
  // Dwa zakresy [Afrom, Ato] i [Bfrom, Bto] nakładają się jeśli:
  // Afrom <= Bto i Bfrom <= Ato
  // Traktujemy end_date jako dzień włącznie (inclusive)
  return rangeA.activeFrom <= rangeB.activeTo && rangeB.activeFrom <= rangeA.activeTo;
}

// Funkcja analizująca kombinacje leków dla pojedynczego leku
async function analyzeMedicationCombinations(
  userId: string,
  medication: any,
  allMedications: any[]
): Promise<MedicationCombinationAnalysis | undefined> {
  try {
    // Pobierz wszystkie leki (LEK), nie suplementy, z wykluczeniem aktualnego leku
    const otherMedications = allMedications.filter(
      m => m.category === 'LEK' && m.id !== medication.id
    );
    
    if (otherMedications.length === 0) {
      return {
        hasCombinations: false,
        combinations: [],
        summary: 'W dostępnych danych ten lek nie pokrywa się czasowo z innymi lekami.'
      };
    }
    
    // Wyznacz zakres aktywności aktualnego leku
    const currentRange = getActiveRange(medication);
    
    if (!currentRange.isValid) {
      return {
        hasCombinations: false,
        combinations: [],
        summary: 'Nie da się ocenić jednoczesnego stosowania, bo brakuje pełnych dat dla tego leku.'
      };
    }
    
    // Sprawdź które inne leki mają nakładające się zakresy
    const overlappingMeds: Array<{ medication: any; range: ActiveRange }> = [];
    const invalidRanges: Array<{ medication: any; range: ActiveRange }> = [];
    
    for (const otherMed of otherMedications) {
      const otherRange = getActiveRange(otherMed);
      if (!otherRange.isValid) {
        invalidRanges.push({ medication: otherMed, range: otherRange });
      } else if (rangesOverlap(currentRange, otherRange)) {
        overlappingMeds.push({ medication: otherMed, range: otherRange });
      }
    }
    
    // Jeśli są inne leki, ale mają invalidRange
    if (overlappingMeds.length === 0 && invalidRanges.length > 0) {
      return {
        hasCombinations: false,
        combinations: [],
        summary: 'Nie da się ocenić jednoczesnego stosowania, bo brakuje pełnych dat dla innych leków.'
      };
    }
    
    // Jeśli rzeczywiście brak nakładania się
    if (overlappingMeds.length === 0) {
      return {
        hasCombinations: false,
        combinations: [],
        summary: 'W dostępnych danych ten lek nie pokrywa się czasowo z innymi lekami.'
      };
    }
    
    // Oblicz wszystkie możliwe kombinacje innych leków (1 leku, 2 leków, 3 leków, itd.)
    // Używamy tylko obliczeń matematycznych na zakresach dat, bez iteracji dzień po dniu
    
    // Generuj wszystkie możliwe kombinacje innych leków (1, 2, 3, ... leków jednocześnie)
    const generateCombinations = (items: Array<{ medication: any; range: ActiveRange }>, maxSize: number): Array<Array<{ medication: any; range: ActiveRange }>> => {
      const combinations: Array<Array<{ medication: any; range: ActiveRange }>> = [];
      
      // Kombinacje pojedynczych leków
      for (let i = 0; i < items.length; i++) {
        combinations.push([items[i]]);
      }
      
      // Kombinacje 2, 3, ... leków (do maxSize)
      for (let size = 2; size <= Math.min(maxSize, items.length); size++) {
        const generate = (start: number, current: Array<{ medication: any; range: ActiveRange }>) => {
          if (current.length === size) {
            combinations.push([...current]);
            return;
          }
          for (let i = start; i < items.length; i++) {
            current.push(items[i]);
            generate(i + 1, current);
            current.pop();
          }
        };
        generate(0, []);
      }
      
      return combinations;
    };
    
    // Generuj kombinacje (maksymalnie 3 leki jednocześnie dla wydajności)
    const allCombinations = generateCombinations(overlappingMeds, 3);
    
    // Dla każdej kombinacji oblicz przecięcie zakresów
    const combinationGroups = new Map<string, {
      otherMedications: Array<{ medication: any; range: ActiveRange }>;
      intersection: { start: Date; end: Date; days: number } | null;
      otherMedNames: string[];
    }>();
    
    for (const combo of allCombinations) {
      // Oblicz przecięcie wszystkich zakresów w kombinacji
      let intersection: { start: Date; end: Date; days: number } | null = getRangeIntersection(currentRange, combo[0].range);
      
      for (let i = 1; i < combo.length && intersection; i++) {
        const nextIntersection = getRangeIntersection(
          { activeFrom: intersection.start, activeTo: intersection.end, isValid: true },
          combo[i].range
        );
        if (!nextIntersection) {
          intersection = null;
          break;
        }
        intersection = nextIntersection;
      }
      
      if (intersection && intersection.days >= 1) {
        const key = combo.map(c => c.medication.id).sort().join(',');
        const medNames = combo.map(c => c.medication.name).sort();
        
        if (!combinationGroups.has(key)) {
          combinationGroups.set(key, {
            otherMedications: combo,
            intersection,
            otherMedNames: medNames
          });
        }
      }
    }
    
    if (combinationGroups.size === 0) {
      return {
        hasCombinations: false,
        combinations: [],
        summary: 'W dostępnych danych ten lek nie pokrywa się czasowo z innymi lekami.'
      };
    }
    
    // Oblicz okresy solo (gdzie aktualny lek jest aktywny, ale żadna kombinacja nie jest aktywna)
    // Okres solo = zakres aktualnego leku minus wszystkie przecięcia z kombinacjami
    const soloRanges: Array<{ start: Date; end: Date; days: number }> = [];
    
    // Zbierz wszystkie daty graniczne (start i end) z zakresu aktualnego leku i wszystkich przecięć
    const boundaryDates = new Set<number>();
    boundaryDates.add(currentRange.activeFrom.getTime());
    boundaryDates.add(currentRange.activeTo.getTime());
    
    for (const group of Array.from(combinationGroups.values())) {
      if (group.intersection) {
        boundaryDates.add(group.intersection.start.getTime());
        boundaryDates.add(group.intersection.end.getTime());
      }
    }
    
    const sortedBoundaries = Array.from(boundaryDates).sort((a, b) => a - b);
    
    // Sprawdź każdy segment między granicami czy jest solo czy combination
    for (let i = 0; i < sortedBoundaries.length - 1; i++) {
      const segmentStart = new Date(sortedBoundaries[i]);
      const segmentEnd = new Date(sortedBoundaries[i + 1]);
      
      // Sprawdź czy ten segment jest w zakresie aktualnego leku
      if (segmentStart < currentRange.activeFrom || segmentEnd > currentRange.activeTo) {
        continue;
      }
      
      // Sprawdź czy ten segment jest pokryty przez jakąkolwiek kombinację
      let isCoveredByCombination = false;
      for (const group of Array.from(combinationGroups.values())) {
        if (group.intersection && 
            segmentStart >= group.intersection.start && 
            segmentEnd <= group.intersection.end) {
          isCoveredByCombination = true;
          break;
        }
      }
      
      if (!isCoveredByCombination) {
        const days = daysBetween(segmentStart, segmentEnd);
        if (days >= 1) {
          soloRanges.push({ start: segmentStart, end: segmentEnd, days });
        }
      }
    }
    
    // Oblicz całkowite okresy solo (suma wszystkich soloRanges)
    const totalSoloDays = soloRanges.reduce((sum, range) => sum + range.days, 0);
    const totalSoloStart = soloRanges.length > 0
      ? new Date(Math.min(...soloRanges.map(r => r.start.getTime())))
      : null;
    const totalSoloEnd = soloRanges.length > 0
      ? new Date(Math.max(...soloRanges.map(r => r.end.getTime())))
      : null;
    
    const combinations: MedicationCombination[] = [];
    
    for (const group of Array.from(combinationGroups.values())) {
      if (!group.intersection || group.intersection.days < 1) continue;
      
      // Okres solo: suma wszystkich soloRanges
      // Okres combination: przecięcie dla tej kombinacji
      
      if (!totalSoloStart || !totalSoloEnd || totalSoloDays < 1) continue;
      
      const soloStartStr = toDateOnlyLocal(totalSoloStart);
      const soloEndStr = toDateOnlyLocal(totalSoloEnd);
      const comboStartStr = toDateOnlyLocal(group.intersection.start);
      const comboEndStr = toDateOnlyLocal(group.intersection.end);
      
      // Pobierz dane dla okresów
      const soloDays = await getDaySummaries(userId, soloStartStr, soloEndStr);
      const comboDays = await getDaySummaries(userId, comboStartStr, comboEndStr);
      
      // Wymagamy minimum 1 dnia w każdym okresie dla analizy
      if (soloDays.length < 1 || comboDays.length < 1) continue;
      
      // Oblicz metryki dla okresu samodzielnego
      const soloFlareDays = soloDays.filter(d => isFlareDay(d)).length;
      const soloAvgBm = soloDays.reduce((sum, d) => sum + d.bmCount, 0) / soloDays.length;
      const soloBloodDays = soloDays.filter(d => d.bloodPresentDay).length;
      const soloAvgPain = soloDays.reduce((sum, d) => sum + d.painAvgDay, 0) / soloDays.length;
      const soloAvgUrgency = soloDays.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / soloDays.length;
      
      const soloWellbeing = await dailyTrackingService.getRange(userId, soloStartStr, soloEndStr);
      const soloEnergyLevels = soloWellbeing
        .filter(w => w.energyLevel !== undefined)
        .map(w => w.energyLevel || 0);
      const soloAvgEnergy = soloEnergyLevels.length > 0
        ? soloEnergyLevels.reduce((sum, e) => sum + e, 0) / soloEnergyLevels.length
        : 0;
      
      // Oblicz metryki dla okresu w połączeniu
      const comboFlareDays = comboDays.filter(d => isFlareDay(d)).length;
      const comboAvgBm = comboDays.reduce((sum, d) => sum + d.bmCount, 0) / comboDays.length;
      const comboBloodDays = comboDays.filter(d => d.bloodPresentDay).length;
      const comboAvgPain = comboDays.reduce((sum, d) => sum + d.painAvgDay, 0) / comboDays.length;
      const comboAvgUrgency = comboDays.reduce((sum, d) => sum + d.urgencyAvgDay, 0) / comboDays.length;
      
      const comboWellbeing = await dailyTrackingService.getRange(userId, comboStartStr, comboEndStr);
      const comboEnergyLevels = comboWellbeing
        .filter(w => w.energyLevel !== undefined)
        .map(w => w.energyLevel || 0);
      const comboAvgEnergy = comboEnergyLevels.length > 0
        ? comboEnergyLevels.reduce((sum, e) => sum + e, 0) / comboEnergyLevels.length
        : 0;
      
      // Oblicz różnicę
      const flareDaysChange = comboFlareDays - soloFlareDays;
      const avgBmChange = comboAvgBm - soloAvgBm;
      const bloodDaysChange = comboBloodDays - soloBloodDays;
      const avgPainChange = comboAvgPain - soloAvgPain;
      const avgUrgencyChange = comboAvgUrgency - soloAvgUrgency;
      const avgEnergyChange = comboAvgEnergy - soloAvgEnergy;
      
      const difference = {
        flareDaysChange,
        avgBmChange,
        bloodDaysChange,
        avgPainChange,
        avgUrgencyChange,
        avgEnergyChange
      };
      
      // Określ wniosek
      let positiveChanges = 0;
      let negativeChanges = 0;
      
      if (flareDaysChange < -1) positiveChanges++;
      else if (flareDaysChange > 1) negativeChanges++;
      if (avgBmChange < -0.5) positiveChanges++;
      else if (avgBmChange > 0.5) negativeChanges++;
      if (bloodDaysChange < 0) positiveChanges++;
      else if (bloodDaysChange > 0) negativeChanges++;
      if (avgPainChange < -0.5) positiveChanges++;
      else if (avgPainChange > 0.5) negativeChanges++;
      if (avgUrgencyChange < -0.5) positiveChanges++;
      else if (avgUrgencyChange > 0.5) negativeChanges++;
      if (avgEnergyChange > 0.5) positiveChanges++;
      else if (avgEnergyChange < -0.5) negativeChanges++;
      
      let conclusion: 'improvement' | 'no_change' | 'worsening' | 'insufficient_data';
      if (positiveChanges >= 3 && negativeChanges <= 1) {
        conclusion = 'improvement';
      } else if (negativeChanges >= 3 && positiveChanges <= 1) {
        conclusion = 'worsening';
      } else {
        conclusion = 'no_change';
      }
      
      // Generuj podsumowanie opisowe
      let summary = '';
      if (conclusion === 'improvement') {
        summary = `Objawy były łagodniejsze, gdy lek był stosowany w połączeniu z innymi lekami.`;
      } else if (conclusion === 'worsening') {
        summary = `Objawy były bardziej nasilone podczas stosowania leku w połączeniu.`;
      } else {
        summary = `Nie zaobserwowano wyraźnej różnicy między stosowaniem samodzielnym a w połączeniu.`;
      }
      
      combinations.push({
        combinationName: `${medication.name} + ${group.otherMedNames.join(' + ')}`,
        otherMedications: group.otherMedNames,
        soloPeriod: {
          daysWithFlare: soloFlareDays,
          avgBmPerDay: soloAvgBm,
          bloodDaysCount: soloBloodDays,
          avgPain: soloAvgPain,
          avgUrgency: soloAvgUrgency,
          avgEnergy: soloAvgEnergy,
          periodStart: soloStartStr,
          periodEnd: soloEndStr,
          daysCount: soloDays.length
        },
        combinationPeriod: {
          daysWithFlare: comboFlareDays,
          avgBmPerDay: comboAvgBm,
          bloodDaysCount: comboBloodDays,
          avgPain: comboAvgPain,
          avgUrgency: comboAvgUrgency,
          avgEnergy: comboAvgEnergy,
          periodStart: comboStartStr,
          periodEnd: comboEndStr,
          daysCount: comboDays.length
        },
        difference,
        conclusion,
        summary
      });
    }
    
    if (combinations.length === 0) {
      return {
        hasCombinations: false,
        combinations: [],
        summary: 'Ten lek był stosowany wyłącznie samodzielnie.'
      };
    }
    
    // Generuj ogólne podsumowanie
    const improvementCount = combinations.filter(c => c.conclusion === 'improvement').length;
    const worseningCount = combinations.filter(c => c.conclusion === 'worsening').length;
    
    let generalSummary = '';
    if (improvementCount > worseningCount) {
      generalSummary = `W większości kombinacji obserwowano łagodniejsze objawy podczas stosowania w połączeniu.`;
    } else if (worseningCount > improvementCount) {
      generalSummary = `W większości kombinacji obserwowano bardziej nasilone objawy podczas stosowania w połączeniu.`;
    } else {
      generalSummary = `Nie zaobserwowano wyraźnych różnic między stosowaniem samodzielnym a w połączeniu.`;
    }
    
    return {
      hasCombinations: true,
      combinations,
      summary: generalSummary
    };
  } catch (error) {
    console.error('Błąd analizy kombinacji leków:', error);
    return undefined;
  }
}

export const medicationHistoryService = {
  analyzeMedicationHistory,
  analyzeSupplementHistory,
  analyzeMedicationCombinations
};
